import { useCallback, useRef } from 'react';
import { episodeScriptApi } from '../../services/user';
import { projectAssetApi } from '../../services/user/projectAsset.api';
import { AssetType } from '@packages/global';
import { useToast } from '../../stores/toastStore';

interface UseChatPanelApplyOptions {
  scriptId: string | undefined;
  script: any;
  currentStep: number;
  refreshScript: () => Promise<void>;
  episodeId: string | undefined;
  projectId: string | undefined;
  /** 刷新 episodeAssets 列表 */
  refreshEpisodeAssets?: () => Promise<void>;
}

export function useChatPanelApply({ scriptId, script, currentStep, refreshScript, episodeId, projectId, refreshEpisodeAssets }: UseChatPanelApplyOptions) {
  const toast = useToast();

  // 用 ref 持有最新值，避免 useCallback 依赖频繁变化
  const scriptRef = useRef(script);
  scriptRef.current = script;
  const currentStepRef = useRef(currentStep);
  currentStepRef.current = currentStep;
  const scriptIdRef = useRef(scriptId);
  scriptIdRef.current = scriptId;
  const episodeIdRef = useRef(episodeId);
  episodeIdRef.current = episodeId;
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;
  const refreshScriptRef = useRef(refreshScript);
  refreshScriptRef.current = refreshScript;
  const refreshEpisodeAssetsRef = useRef(refreshEpisodeAssets);
  refreshEpisodeAssetsRef.current = refreshEpisodeAssets;

  // Step 1/2/6 通用 apply（直接字段映射）- 稳定引用
  const handleSimpleApply = useCallback(async (data: any) => {
    const sid = scriptIdRef.current;
    if (!sid) return;
    try {
      const fieldMap: Record<number, string[]> = {
        1: ['title', 'summary', 'highlights', 'language'],
        2: ['styleName', 'artStyle', 'basePrompt', 'bgmId', 'bgmUrl', 'narratorVoiceUrl'],
        6: ['shots'],
      };
      const fields = fieldMap[currentStepRef.current] || [];
      const updates = Object.fromEntries(fields.filter(f => f in data).map(f => [f, data[f]]));
      await episodeScriptApi.updateEpisodeScript(sid, updates);
      await refreshScriptRef.current();
      toast.success('已应用并保存');
    } catch (err: any) {
      toast.error(err.message || '保存失败');
    }
  }, [toast]);

  // 角色 apply - 操作 ProjectAsset
  const handleCharactersApply = useCallback(async (action: { type: 'create' | 'update' | 'delete'; character?: any; index?: number }) => {
    const eid = episodeIdRef.current;
    const pid = projectIdRef.current;
    if (!eid || !pid) return;
    try {
      if (action.type === 'create' && action.character) {
        const item = action.character;
        await projectAssetApi.createProjectAsset({
          projectId: pid,
          episodeId: eid,
          assetType: AssetType.CHARACTER,
          name: item.name,
          description: item.description || '',
          prompt: item.prompt || '',
          conceptImageUrl: item.imageUrl,
          voiceReference: item.audioUrl,
          voiceModelId: item.voiceId,
        });
      } else if (action.type === 'update' && action.character) {
        // 优先用 entityId（ProjectAsset.id）直接更新，避免按名字查找失败
        const entityId = action.character.entityId;
        if (entityId) {
          await projectAssetApi.updateProjectAsset(entityId, {
            // 名称保持不变（不传 name），只更新 prompt/description/conceptImageUrl
            description: action.character.description || '',
            prompt: action.character.prompt || '',
            // LLM 更新了提示词，旧图片已不匹配，清空 conceptImageUrl
            conceptImageUrl: '',
            voiceReference: action.character.audioUrl,
            voiceModelId: action.character.voiceId,
          });
        } else {
          // 兜底：按名称查找（旧数据兼容）
          const assets = await projectAssetApi.getEpisodeAssets(eid);
          const target = assets.find(a => a.assetType === AssetType.CHARACTER && a.name === action.character!.name);
          if (!target) { toast.error(`角色「${action.character.name}」不存在`); return; }
          await projectAssetApi.updateProjectAsset(target.id, {
            description: action.character.description || '',
            prompt: action.character.prompt || '',
            conceptImageUrl: '',
            voiceReference: action.character.audioUrl,
            voiceModelId: action.character.voiceId,
          });
        }
      } else if (action.type === 'delete' && action.character?.name) {
        const entityId = action.character.entityId;
        if (entityId) {
          await projectAssetApi.deleteProjectAsset(entityId);
        } else {
          const assets = await projectAssetApi.getEpisodeAssets(eid);
          const target = assets.find(a => a.assetType === AssetType.CHARACTER && a.name === action.character!.name);
          if (!target) { toast.error(`角色「${action.character.name}」不存在`); return; }
          await projectAssetApi.deleteProjectAsset(target.id);
        }
      }
      await refreshEpisodeAssetsRef.current?.();
      await refreshScriptRef.current();
    } catch (err: any) {
      toast.error(err.message || '操作失败');
    }
  }, [toast]);

  // 道具 apply - 操作 ProjectAsset
  const handlePropsApply = useCallback(async (action: { type: 'create' | 'update' | 'delete'; prop?: any; index?: number }) => {
    const eid = episodeIdRef.current;
    const pid = projectIdRef.current;
    if (!eid || !pid) return;
    try {
      if (action.type === 'create' && action.prop) {
        const item = action.prop;
        await projectAssetApi.createProjectAsset({
          projectId: pid,
          episodeId: eid,
          assetType: AssetType.PROP,
          name: item.name,
          description: item.description || '',
          prompt: item.prompt || '',
          conceptImageUrl: item.imageUrl,
        });
      } else if (action.type === 'update' && action.prop) {
        const entityId = action.prop.entityId;
        if (entityId) {
          await projectAssetApi.updateProjectAsset(entityId, {
            description: action.prop.description || '',
            prompt: action.prop.prompt || '',
            conceptImageUrl: '',
          });
        } else {
          const assets = await projectAssetApi.getEpisodeAssets(eid);
          const target = assets.find(a => a.assetType === AssetType.PROP && a.name === action.prop!.name);
          if (!target) { toast.error(`道具「${action.prop.name}」不存在`); return; }
          await projectAssetApi.updateProjectAsset(target.id, {
            description: action.prop.description || '',
            prompt: action.prop.prompt || '',
            conceptImageUrl: '',
          });
        }
      } else if (action.type === 'delete' && action.prop?.name) {
        const entityId = action.prop.entityId;
        if (entityId) {
          await projectAssetApi.deleteProjectAsset(entityId);
        } else {
          const assets = await projectAssetApi.getEpisodeAssets(eid);
          const target = assets.find(a => a.assetType === AssetType.PROP && a.name === action.prop!.name);
          if (!target) { toast.error(`道具「${action.prop.name}」不存在`); return; }
          await projectAssetApi.deleteProjectAsset(target.id);
        }
      }
      await refreshEpisodeAssetsRef.current?.();
      await refreshScriptRef.current();
    } catch (err: any) {
      toast.error(err.message || '操作失败');
    }
  }, [toast]);

  // 场景 apply - 操作 ProjectAsset
  const handleScenesApply = useCallback(async (action: { type: 'create' | 'update' | 'delete'; scene?: any; index?: number }) => {
    const eid = episodeIdRef.current;
    const pid = projectIdRef.current;
    if (!eid || !pid) return;
    try {
      if (action.type === 'create' && action.scene) {
        const item = action.scene;
        await projectAssetApi.createProjectAsset({
          projectId: pid,
          episodeId: eid,
          assetType: AssetType.SCENE,
          name: item.name,
          description: item.description || '',
          prompt: item.prompt || '',
          conceptImageUrl: item.imageUrl,
          location: item.location,
        });
      } else if (action.type === 'update' && action.scene) {
        const entityId = action.scene.entityId;
        if (entityId) {
          await projectAssetApi.updateProjectAsset(entityId, {
            description: action.scene.description || '',
            prompt: action.scene.prompt || '',
            conceptImageUrl: '',
            location: action.scene.location,
          });
        } else {
          const assets = await projectAssetApi.getEpisodeAssets(eid);
          const target = assets.find(a => a.assetType === AssetType.SCENE && a.name === action.scene!.name);
          if (!target) { toast.error(`场景「${action.scene.name}」不存在`); return; }
          await projectAssetApi.updateProjectAsset(target.id, {
            description: action.scene.description || '',
            prompt: action.scene.prompt || '',
            conceptImageUrl: '',
            location: action.scene.location,
          });
        }
      } else if (action.type === 'delete' && action.scene?.name) {
        const entityId = action.scene.entityId;
        if (entityId) {
          await projectAssetApi.deleteProjectAsset(entityId);
        } else {
          const assets = await projectAssetApi.getEpisodeAssets(eid);
          const target = assets.find(a => a.assetType === AssetType.SCENE && a.name === action.scene!.name);
          if (!target) { toast.error(`场景「${action.scene.name}」不存在`); return; }
          await projectAssetApi.deleteProjectAsset(target.id);
        }
      }
      await refreshEpisodeAssetsRef.current?.();
      await refreshScriptRef.current();
    } catch (err: any) {
      toast.error(err.message || '操作失败');
    }
  }, [toast]);

  // 分镜 apply - 稳定引用
  const handleShotsApply = useCallback(async (action: { type: 'create' | 'update' | 'delete'; index?: number; shot?: any }) => {
    const sid = scriptIdRef.current;
    if (!sid) return;
    try {
      const shots = [...(scriptRef.current?.shots || [])];
      if (action.type === 'create' && action.shot) {
        if (action.index !== undefined && action.index >= 0 && action.index <= shots.length)
          shots.splice(action.index, 0, action.shot);
        else shots.push(action.shot);
      } else if (action.type === 'update' && action.shot && action.index !== undefined) {
        shots[action.index] = action.shot;
      } else if (action.type === 'delete' && action.index !== undefined) {
        shots.splice(action.index, 1);
      }
      await episodeScriptApi.updateEpisodeScript(sid, { shots });
      await refreshScriptRef.current();
      toast.success('已应用');
    } catch (err: any) {
      toast.error(err.message || '操作失败');
    }
  }, [toast]);

  return {
    handleSimpleApply,
    handleCharactersApply,
    handlePropsApply,
    handleScenesApply,
    handleShotsApply,
  };
}
